import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { LayoutDashboard, Calendar, Users, ScanLine, Receipt, LogOut, Settings, Tag, Clock, Map, Building2, Quote as QuoteIcon, UserCheck, Banknote } from "lucide-react";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const isHandler = user?.role === "check_in";
  const isOrganizer = user?.role === "organizer";

  useEffect(() => {
    // If event handler tries to access other admin screens, redirect to check-in scanner
    if (isHandler && loc.pathname !== "/admin/check-in") {
      nav("/admin/check-in", { replace: true });
    }
  }, [isHandler, loc.pathname, nav]);

  const navItems = isHandler
    ? [{ to: "/admin/check-in", label: "Check-in Scanner", icon: ScanLine, tid: "adm-nav-checkin" }]
    : isOrganizer
    ? [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "adm-nav-dash" },
        { to: "/admin/events", label: "My Events", icon: Calendar, tid: "adm-nav-events" },
        { to: "/admin/bookings", label: "Attendees & Orders", icon: Users, tid: "adm-nav-bookings" },
        { to: "/admin/offline-sales", label: "Offline & Bulk Passes", icon: Banknote, tid: "adm-nav-offline" },
        { to: "/admin/check-in", label: "Check-in Scanner", icon: ScanLine, tid: "adm-nav-checkin" },
        { to: "/admin/organizer-settings", label: "Gateway & Branding", icon: Settings, tid: "adm-nav-org-settings" },
      ]
    : [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "adm-nav-dash" },
        { to: "/admin/events", label: "Events", icon: Calendar, tid: "adm-nav-events" },
        { to: "/admin/bookings", label: "Bookings", icon: Users, tid: "adm-nav-bookings" },
        { to: "/admin/offline-sales", label: "Offline & Bulk Passes", icon: Banknote, tid: "adm-nav-offline" },
        { to: "/admin/check-in", label: "Check-in", icon: ScanLine, tid: "adm-nav-checkin" },
        ...(user?.role === "super_admin"
          ? [
              { to: "/admin/organizers", label: "Organizers", icon: Building2, tid: "adm-nav-organizers" },
              { to: "/admin/event-handlers", label: "Event Handlers", icon: UserCheck, tid: "adm-nav-handlers" },
            ]
          : []),
        { to: "/admin/finance", label: "Finance", icon: Receipt, tid: "adm-nav-finance" },
        { to: "/admin/coupons", label: "Coupons", icon: Tag, tid: "adm-nav-coupons" },
        { to: "/admin/waitlist", label: "Waitlist", icon: Clock, tid: "adm-nav-waitlist" },
        { to: "/admin/cities", label: "Cities", icon: Map, tid: "adm-nav-cities" },
        { to: "/admin/venues", label: "Venues", icon: Building2, tid: "adm-nav-venues" },
        { to: "/admin/quotes", label: "Quotes", icon: QuoteIcon, tid: "adm-nav-quotes" },
        { to: "/admin/settings", label: "Settings", icon: Settings, tid: "adm-nav-settings" },
      ];

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      <div className="flex">
        <aside className="hidden md:flex md:w-60 bg-card border-r border-border min-h-screen flex-col">
          <div className="px-5 py-5 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[color:var(--brand-terracotta)] flex items-center justify-center text-white font-display font-black text-sm">
                {isOrganizer ? (user?.organizer_slug?.slice(0, 2).toUpperCase() || "OB") : "CG"}
              </div>
              <div>
                <div className="font-display font-bold text-sm">
                  {isOrganizer ? (user?.name || "Organizer") : "Common Ground"}
                </div>
                <div className="text-[10px] font-mono text-stone-500 uppercase tracking-widest">
                  {isHandler ? "Event Handler" : isOrganizer ? "Organizer Portal" : "Admin"}
                </div>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {navItems.map((it) => (
              <NavLink key={it.to} end={it.end} to={it.to} data-testid={it.tid}
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm ${isActive ? "bg-[color:var(--brand-terracotta)] text-white" : "hover:bg-secondary"}`}>
                <it.icon size={16} /> {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-border">
            <div className="px-3 pb-3 text-xs">
              <div className="font-semibold truncate">{user?.name}</div>
              <div className="text-stone-500 truncate">{isHandler ? "Event Handler (Scanner)" : user?.role?.replace("_", " ")}</div>
            </div>
            <button data-testid="admin-logout" onClick={() => { logout(); nav("/admin/login"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-secondary">
              <LogOut size={14}/> Sign out
            </button>
          </div>
        </aside>
        <main className="flex-1 min-w-0">
          {/* mobile nav */}
          <div className="md:hidden border-b border-border sticky top-0 bg-background/90 backdrop-blur z-30">
            <div className="flex overflow-x-auto gap-1 px-2 py-2">
              {navItems.map((it) => (
                <NavLink key={it.to} end={it.end} to={it.to} className={({isActive}) => `whitespace-nowrap px-3 py-1.5 rounded text-xs ${isActive?"bg-[color:var(--brand-terracotta)] text-white":"bg-secondary"}`}>
                  {it.label}
                </NavLink>
              ))}
              <button onClick={() => { logout(); nav("/admin/login"); }} className="whitespace-nowrap px-3 py-1.5 rounded text-xs bg-secondary">Sign out</button>
            </div>
          </div>
          <div className="p-5 md:p-8"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}
